import { DripIdentifyData } from '@xylabs/react-pixel'
import { Buffer } from 'buffer/'

enum DripTags {
  //this is used to start abandoned cart
  FunnelComplete = 'coin-free-plus-complete',
  FunnelStart = 'coin-free-plus-start', // this prevents abandoned cart emails
  EmailCollected = 'coin-signup-start',
  AccountCreated = 'coin-signup-complete',
}

import { UniqueUserId } from '@xylabs/pixel'
import { delay } from '@xylabs/sdk-js'

import { Cart, Funnel } from '../hooks'
import { AddressInfoData } from '../models'
import { ContactInfoWrapper } from '../wrappers'
import { CoinUserEventHandlerBase } from './CoinUserEventHandlerBase'
import { DripEvents } from './Drip'

const emptyPromise = () => {
  return new Promise<void>(() => {
    return
  })
}

export class DripEventHandler<T> extends CoinUserEventHandlerBase<T> {
  async emailCollected({ email }: { email: string }): Promise<void> {
    const payload = this.buildAccountPayload(DripTags.EmailCollected, email)
    await this.events.emailCollected().send(payload)
    await this.events.identify().send(payload)
  }
  async accountCreated(): Promise<void> {
    await this.events.accountCreated().send(this.buildAccountPayload(DripTags.AccountCreated))
    return this.events.identify().send(this.buildAccountPayload(DripTags.AccountCreated))
  }
  shareCompleted(): Promise<void> {
    return emptyPromise()
  }
  buyticketsCompleted(): Promise<void> {
    return emptyPromise()
  }
  installCompleted(): Promise<void> {
    return emptyPromise()
  }
  funnelCompleted(): Promise<void> {
    return emptyPromise()
  }
  offerRequested(_fields: { id: string }): Promise<void> {
    return emptyPromise()
  }
  feedbackScoreCollected(_fields: { score: string | number }): Promise<void> {
    return emptyPromise()
  }
  feedbackSourceCollected(_fields: { source: string }): Promise<void> {
    return emptyPromise()
  }
  goodFeedbackRequested(): Promise<void> {
    return emptyPromise()
  }
  badFeedbackRequested(): Promise<void> {
    return emptyPromise()
  }
  private events = new DripEvents()

  contactInfoComplete() {
    const data = this.buildIdentifyPayload(DripTags.FunnelStart)
    return this.events.identify().send(data)
  }

  addressInfoComplete() {
    const data = this.buildIdentifyPayload(DripTags.FunnelStart)
    return this.events.identify().send(data)
  }

  paymentInfoComplete() {
    const data = this.buildIdentifyPayload(DripTags.FunnelStart)
    return this.events.identify().send(data)
  }

  async customEvent(name: string, data: T) {
    const casted = data as Record<string, unknown>
    await this.events.customEvent(name).send(casted)
  }

  async purchaseCompleted() {
    const cart = Cart.get()
    const data = this.buildIdentifyPayload(DripTags.FunnelComplete)
    const value = cart.getValue() * 100 //drip wants it in cents
    await this.events.identify().send(data)
    await this.events.purchase().send({ value })
  }

  buildIdentifyPayload(tag: DripTags, addressInfo?: AddressInfoData) {
    const cart = Cart.get()
    const contactInfo = new ContactInfoWrapper().load()
    const contactInfoData = contactInfo?.getData()
    const coinId = new UniqueUserId().id
    const funnel = Funnel.get().data

    const tags: string[] = [tag]
    const email = contactInfoData.email
    const address = addressInfo?.billing

    if (!email) {
      throw Error('Missing email')
    }

    for (const item of cart.getItems() ?? []) {
      tags.push(item.planId)
    }

    const payload: DripIdentifyData = {
      address1: address?.address,
      address2: address?.address2,
      city: address?.city,
      country: address?.country,
      email,
      first_name: contactInfoData.firstName,
      last_name: contactInfoData.lastName,
      phone: contactInfoData.phone,
      state: address?.region,
      tags,
      user_id: coinId,
      zip: address?.postalCode,
      ...funnel,
    }

    payload.cart64 = Buffer.from(JSON.stringify(payload)).toString('base64')

    return payload
  }

  buildAccountPayload(tags: DripTags, email?: string) {
    const coinId = new UniqueUserId().id
    return { email, tags: [tags], user_id: coinId }
  }

  public async addToCart() {
    await delay(0)
  }

  public async initiateCheckout() {
    await delay(0)
  }
  public async purchaseAttempted() {
    await delay(0)
  }
  public async trialStarted() {
    await delay(0)
  }
  public async purchaseFailed() {
    await delay(0)
  }
  public async upsellViewed() {
    await delay(0)
  }
  public async upsellUnavailable() {
    await delay(0)
  }
  public async upsellAccepted() {
    await delay(0)
  }
  public async upsellSucceeded() {
    await delay(0)
  }
  public async upsellFailed() {
    await delay(0)
  }
  public async funnelStarted() {
    await delay(0)
  }
  public async testStarted() {
    await delay(0)
  }
  public async userClick() {
    await delay(0)
  }
  public async viewContent() {
    await delay(0)
  }
}
